import React, { useMemo, useRef, useState, useEffect } from "react";

import { SkeletonRows } from "@Components/shared/SkeletonRows";
import CountrySelect from "@Components/user/CountrySelect";
import GenderSelect from "@Components/user/GenderSelect";
import SpecialtySelect from "@Components/user/SpecialtySelect";
import { QueryKeys, ThreeMinutes, TermsTypes } from "@Constants";
import SessionManager from "@Core/session";
import { IUserDetails, IUserDetailsSchema } from "@Models/IUserDetails";
import UserService from "@Services/UserService";
import { LoadingOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, DatePicker, Divider, Form, Input, InputNumber, notification, Progress, Typography } from "antd";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import moment from "moment";
import { disableUnder10YearsAgo, getProgressClass, calculateFormCompletion } from "@Utils";

import "@Styles/components/accountDetailsForm.scss";
import { useGetUserAcceptedTerms, useHasFeature } from "@Components/shared/queries";

const AccountDetailsTab: React.FC = () => {
    const { Title } = Typography;
    const dateFormatList: string[] = ["DD/MM/YYYY", "DD/MM/YY"];
    const queryClient = useQueryClient();
    const [userData, setUserData] = useState<IUserDetails | null>(
        queryClient.getQueryData<IUserDetails>([QueryKeys.UserAccountDetailsPrivate]) ?? null
    );
    const { data: hasGraduationYearField } = useHasFeature("graduation-year-field");

    const [dirtyFields, setDirtyFields] = useState<string[]>([]);
    const [hasAcceptedAllTerms, setHasAcceptedAllTerms] = useState(false);

    // Get latest user accepted terms
    const { data: acceptedTerms } = useGetUserAcceptedTerms(TermsTypes.Terms, false);
    const { data: acceptedPrivacyPolicy } = useGetUserAcceptedTerms(TermsTypes.PrivacyPolicy, false);

    // Check if user has accepted any of both terms
    useEffect(() => {
        const hasAcceptedTerms = acceptedTerms?.userAcceptedTerms?.some((term) => term.acceptedOn) ?? false;
        const hasAcceptedPrivacy = acceptedPrivacyPolicy?.userAcceptedTerms?.some((term) => term.acceptedOn) ?? false;

        setHasAcceptedAllTerms(hasAcceptedTerms && hasAcceptedPrivacy);
    }, [acceptedTerms, acceptedPrivacyPolicy]);

    const profileProgress = calculateFormCompletion(userData);

    // Form instance for the account details form
    const [form] = Form.useForm();

    const stateRef = useRef<IUserDetails | null>();
    stateRef.current = userData;

    //Reset form fields
    const resetFormFields = (userData: IUserDetails) => {
        return {
            fullName: userData?.fullName,
            nickname: userData?.nickName,
            email: userData?.email,
            dateOfBirth: moment(userData?.dateOfBirth, moment.ISO_8601),
            gender: userData?.gender,
            countryCode: userData?.countryCode,
            specialty: userData?.specialty,
            yearsTraining: userData?.yearsTraining,
            expectedGraduationYear: userData?.expectedGraduationYear
                ? moment().year(userData.expectedGraduationYear)
                : null,
            medicalSchool: userData?.medicalSchool,
            organisation: userData?.organisation,
        };
    };

    const { isFetching } = useQuery(
        [QueryKeys.UserAccountDetailsPrivate],
        async () => {
            const service = new UserService();
            const user = await service.getPrivate([SessionManager.getServiceUser()?.userId]);
            if (!user.hasErrors) {
                setUserData(user.value[0]);
                resetFormFields(user.value[0]);
                return user.value[0];
            }
            return [];
        },
        {
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
            enabled: !!SessionManager.getServiceUser(),
        }
    );

    /**
     * Trigger an update to the form data state
     * @param field
     * @param value
     */
    const onInputChange = async (field: keyof IUserDetails, value?: string | number | null) => {
        // if the field is required, we don't want to update the state if the value is invalid
        const isValid: boolean = await form
            .validateFields([field])
            .then(() => true) // if an error isn't thrown
            .catch(() => false); // antd throws these validation errors

        // we don't want to update the fields if the value is invalid
        if (!isValid) {
            return;
        }

        setDirtyFields([...dirtyFields, field.toString()]);

        // validate the date against the schema
        const parsedUserDataResult = IUserDetailsSchema.safeParse({ ...userData, [field]: value });

        if (!parsedUserDataResult.success) {
            return;
        }

        setUserData(parsedUserDataResult.data);
        debouncePromise();
    };

    /**
     * Update user details mutation
     */
    const updateUserDetails = useMutation(
        async () => {
            const service = new UserService();

            // if the state is null, we don't want to update the user detail
            if (stateRef.current) {
                const result = await service.update(stateRef.current);
                setDirtyFields([]);
                return result;
            }
            return null;
        },
        {
            onSuccess: async (result) => {
                // Update the service user with the new details
                if (!result.errors.length) {
                    const serviceUser = SessionManager.getServiceUser();
                    if (stateRef.current?.fullName) {
                        serviceUser.fullName = stateRef.current.fullName;
                    }
                    SessionManager.setServiceUser(serviceUser);
                    notification.success({ message: "Account details updated." });
                } else {
                    notification.error({ message: "Account details could not be updated." });
                }
                await queryClient.invalidateQueries([QueryKeys.UserAccountDetailsPrivate]);

                //TODO: this is a temporary solution to delay invalidating the queries until the worker has updated the achievement.
                const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
                await delay(2000);
                await queryClient.invalidateQueries([QueryKeys.Achievement]);
            },
        }
    );

    /**
     * Trigger an update two seconds after last keypress.
     */
    const debouncePromise = useMemo(
        () => AwesomeDebouncePromise(updateUserDetails.mutate, 2000, { onlyResolvesLast: true }),
        [updateUserDetails.mutate]
    );

    const isDirty = (field: string): boolean => {
        return dirtyFields.includes(field);
    };

    // Show a message explaining why graduation year is disabled
    const renderGraduationYearHelp = () => {
        if (!hasAcceptedAllTerms) {
            return (
                <span>
                    Please accept the <a href="/account#privacy">Terms and Conditions and Privacy Policy</a> to set your
                    graduation year.
                </span>
            );
        }
        return null;
    };

    return (
        <div className="account-details">
            <div style={{ width: "100%", paddingBottom: "20px" }}>
                {profileProgress !== null ? (
                    <>
                        <Title level={5}>Your profile progress: {profileProgress}%</Title>
                        <Progress
                            percent={profileProgress}
                            showInfo={false}
                            className={getProgressClass(profileProgress)}
                        />
                    </>
                ) : null}
            </div>
            <Card title="Account Details" className="newUI-raised">
                {isFetching && userData?.fullName === undefined ? (
                    <>
                        <SkeletonRows />
                        <SkeletonRows />
                        <SkeletonRows />
                    </>
                ) : (
                    <Form
                        className="account-details-form"
                        labelAlign="left"
                        form={form}
                        initialValues={{
                            fullName: userData?.fullName,
                            nickname: userData?.nickName,
                            email: userData?.email,
                            dateOfBirth: moment(userData?.dateOfBirth, moment.ISO_8601),
                            gender: userData?.gender,
                            countryCode: userData?.countryCode,
                            specialty: userData?.specialty,
                            yearsTraining: userData?.yearsTraining,
                            expectedGraduationYear: userData?.expectedGraduationYear
                                ? moment().year(userData.expectedGraduationYear)
                                : null,
                            medicalSchool: userData?.medicalSchool,
                            organisation: userData?.organisation,
                        }}
                    >
                        <Form.Item
                            className={`account-details-form-item ${!userData?.fullName ? "empty-field" : ""}`}
                            name="fullName"
                            label={
                                <>
                                    {isDirty("fullName") && (
                                        <LoadingOutlined style={{ fontSize: 16, marginRight: "5px" }} spin />
                                    )}
                                    Full Name
                                </>
                            }
                            required
                            rules={[
                                {
                                    required: true,
                                    message: "Please enter your full name.",
                                },
                            ]}
                        >
                            <Input
                                onChange={(e) => onInputChange("fullName", e.target.value)}
                                placeholder="Please enter your full name."
                            />
                        </Form.Item>
                        <Form.Item
                            className={`account-details-form-item ${!userData?.nickName ? "empty-field" : ""}`}
                            name="nickname"
                            label={
                                <>
                                    {isDirty("nickName") && (
                                        <LoadingOutlined style={{ fontSize: 16, marginRight: "5px" }} spin />
                                    )}
                                    Nickname
                                </>
                            }
                        >
                            <Input
                                onChange={(e) => onInputChange("nickName", e.target.value)}
                                placeholder="Please enter a nickname."
                            />
                        </Form.Item>
                        <Form.Item
                            className={`account-details-form-item ${!userData?.email ? "empty-field" : ""}`}
                            name="email"
                            label={
                                <>
                                    {isDirty("email") && (
                                        <LoadingOutlined style={{ fontSize: 16, marginRight: "5px" }} spin />
                                    )}
                                    Email
                                </>
                            }
                            required
                        >
                            <Input
                                onChange={(e) => onInputChange("email", e.target.value)}
                                placeholder="Please enter a valid email address."
                                disabled
                            />
                        </Form.Item>
                        <Form.Item
                            className={`account-details-form-item ${!userData?.dateOfBirth ? "empty-field" : ""}`}
                            label={
                                <>
                                    {isDirty("dateOfBirth") && (
                                        <LoadingOutlined style={{ fontSize: 16, marginRight: "5px" }} spin />
                                    )}
                                    Date of Birth
                                </>
                            }
                            name="dateOfBirth"
                            rules={[
                                {
                                    required: true,
                                    message: `Please enter a valid date of birth`,
                                },
                            ]}
                        >
                            <DatePicker
                                onChange={(date) => onInputChange("dateOfBirth", date?.toISOString())}
                                picker="date"
                                placeholder="Please enter your date of birth."
                                format={dateFormatList}
                                allowClear={false}
                                disabledDate={disableUnder10YearsAgo}
                            />
                        </Form.Item>
                        <Form.Item
                            className={`account-details-form-item ${!userData?.gender ? "empty-field" : ""}`}
                            name="gender"
                            label={
                                <>
                                    {isDirty("gender") && (
                                        <LoadingOutlined style={{ fontSize: 16, marginRight: "5px" }} spin />
                                    )}
                                    Gender
                                </>
                            }
                        >
                            <GenderSelect
                                value={userData?.gender}
                                onChange={(value) => onInputChange("gender", value)}
                            />
                        </Form.Item>
                        <Form.Item
                            className={`account-details-form-item ${!userData?.organisation ? "empty-field" : ""}`}
                            name="organisation"
                            label={
                                <>
                                    {isDirty("organisation") && (
                                        <LoadingOutlined style={{ fontSize: 16, marginRight: "5px" }} spin />
                                    )}
                                    Organisation
                                </>
                            }
                        >
                            <Input
                                placeholder="Please enter your organisation."
                                onChange={(e) => onInputChange("organisation", e.target.value)}
                            />
                        </Form.Item>
                        <Form.Item
                            className={`account-details-form-item ${!userData?.countryCode ? "empty-field" : ""}`}
                            name="countryCode"
                            label={
                                <>
                                    {isDirty("countryCode") && (
                                        <LoadingOutlined style={{ fontSize: 16, marginRight: "5px" }} spin />
                                    )}
                                    Country
                                </>
                            }
                        >
                            <CountrySelect
                                value={userData?.countryCode}
                                onChange={(value) => onInputChange("countryCode", value)}
                            />
                        </Form.Item>
                        <Divider />
                        <h4>Medical Practice</h4>
                        <Divider />
                        <Form.Item
                            className={`account-details-form-item with-wide-label ${
                                !userData?.specialty ? "empty-field" : ""
                            }`}
                            label={
                                <>
                                    {isDirty("specialty") && (
                                        <LoadingOutlined style={{ fontSize: 16, marginRight: "5px" }} spin />
                                    )}
                                    Medical Specialty
                                </>
                            }
                            name="specialty"
                            rules={[
                                {
                                    required: true,
                                    message: "Please tell us your medical specialty",
                                },
                            ]}
                        >
                            <SpecialtySelect onChange={(value) => onInputChange("specialty", value)} />
                        </Form.Item>

                        <Form.Item
                            className={`account-details-form-item with-wide-label ${
                                !userData?.yearsTraining ? "empty-field" : ""
                            }`}
                            name="yearsTraining"
                            label={
                                <>
                                    {isDirty("yearsTraining") && (
                                        <LoadingOutlined style={{ fontSize: 16, marginRight: "5px" }} spin />
                                    )}
                                    Years of Training
                                </>
                            }
                        >
                            <InputNumber onChange={(value) => onInputChange("yearsTraining", value)} />
                        </Form.Item>

                        {hasGraduationYearField ? (
                            <Form.Item
                                className={`account-details-form-item with-wide-label ${
                                    !userData?.expectedGraduationYear ? "empty-field" : ""
                                }`}
                                name="expectedGraduationYear"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please tell us your residency graduation year",
                                    },
                                ]}
                                label={
                                    <>
                                        {isDirty("expectedGraduationYear") && (
                                            <LoadingOutlined style={{ fontSize: 16, marginRight: "5px" }} spin />
                                        )}
                                        Residency Graduation Year
                                    </>
                                }
                                help={renderGraduationYearHelp()}
                            >
                                <DatePicker
                                    onChange={(date) => onInputChange("expectedGraduationYear", date?.year())}
                                    picker="year"
                                    allowClear={false}
                                    disabled={!hasAcceptedAllTerms}
                                />
                            </Form.Item>
                        ) : null}
                        <Form.Item
                            className={`account-details-form-item with-wide-label ${
                                !userData?.medicalSchool ? "empty-field" : ""
                            }`}
                            name="medicalSchool"
                            label={
                                <>
                                    {isDirty("medicalSchool") && (
                                        <LoadingOutlined style={{ fontSize: 16, marginRight: "5px" }} spin />
                                    )}
                                    Medical School
                                </>
                            }
                        >
                            <Input
                                placeholder="Please enter the medical school you attended."
                                onChange={(e) => onInputChange("medicalSchool", e.target.value)}
                            />
                        </Form.Item>
                    </Form>
                )}
            </Card>
        </div>
    );
};

export default AccountDetailsTab;
